import React from 'react';
import { ActionButton, Card, EmptyState, ErrorState, StatusBadge } from '../admin/InventoryLayout';
import { ACCENT, CardHeader, Stat } from '../../pages/admin/Dashboard';

export const SUPERADMIN_ACCENT = ACCENT;

export const PageHeader = ({ title, description, action }) => (
  <div className="hidden md:block">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{title}</h1>
        {description ? <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">{description}</p> : null}
      </div>
      {action}
    </div>
  </div>
);

export const MetricCard = ({ label, value, icon }) => <Stat label={label} value={value} delta="" icon={icon} />;

export const SectionCard = ({ title, subtitle, icon, action, children, className = '' }) => (
  <Card className={className}>
    <CardHeader title={title} subtitle={subtitle} icon={icon} action={action} />
    <div className="p-3">{children}</div>
  </Card>
);

export const Field = ({ label, children }) => (
  <label className="block space-y-2">
    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{label}</span>
    {children}
  </label>
);

export const TextInput = React.forwardRef(function TextInput(props, ref) {
  const { className = '', style = {}, ...rest } = props;
  return (
    <input
      ref={ref}
      {...rest}
      className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${className}`}
      style={{ '--accent': ACCENT, ...style }}
    />
  );
});

export const SelectInput = (props) => (
  <select
    {...props}
    className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${props.className || ''}`}
    style={{ '--accent': ACCENT, ...(props.style || {}) }}
  />
);

export const TextAreaInput = (props) => (
  <textarea
    {...props}
    className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${props.className || ''}`}
    style={{ '--accent': ACCENT, ...(props.style || {}) }}
  />
);

export const Toolbar = ({ children }) => (
  <Card>
    <div className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between">{children}</div>
  </Card>
);

export const LoadingState = ({ lines = 5, className = '' }) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: lines }, (_, index) => (
      <div
        key={index}
        className="h-12 animate-pulse rounded-xl border border-black/5 bg-white/50 dark:border-white/10 dark:bg-zinc-800/50"
      />
    ))}
  </div>
);

export const PaginationControls = ({ pagination, onPageChange, loading = false }) => {
  if (!pagination || pagination.totalPages <= 1) {
    return null;
  }

  const startPage = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2));
  const pages = Array.from({ length: Math.min(5, pagination.totalPages) }, (_, index) => startPage + index);

  return (
    <div className="flex flex-col gap-3 border-t border-white/10 pt-4 md:flex-row md:items-center md:justify-between">
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <ActionButton
          variant="secondary"
          disabled={loading || pagination.page <= 1}
          onClick={() => onPageChange(pagination.page - 1)}
        >
          Previous
        </ActionButton>
        {pages.map((page) => (
          <button
            key={page}
            type="button"
            disabled={loading}
            onClick={() => onPageChange(page)}
            className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
              page === pagination.page
                ? 'border-[#7d6633] bg-[rgba(125,102,51,0.12)] text-[#7d6633]'
                : 'border-white/10 bg-white/70 text-zinc-700 hover:bg-white dark:bg-zinc-900/60 dark:text-zinc-200 dark:hover:bg-zinc-900'
            } ${loading ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            {page}
          </button>
        ))}
        <ActionButton
          variant="accent"
          disabled={loading || pagination.page >= pagination.totalPages}
          onClick={() => onPageChange(pagination.page + 1)}
        >
          Next
        </ActionButton>
      </div>
    </div>
  );
};

export const KeyValueList = ({ items = [] }) => (
  <dl className="grid gap-3 sm:grid-cols-2">
    {items.map((item) => (
      <div key={item.label} className="rounded-xl border border-black/5 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 p-4">
        <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">{item.label}</dt>
        <dd className="mt-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">{item.value ?? 'N/A'}</dd>
      </div>
    ))}
  </dl>
);

export const DataListTable = ({ columns = [], rows = [], emptyTitle, emptyDescription }) => {
  if (!rows.length) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        icon={
          <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 17v-2m3 2v-4m3 4v-6m2 9H7a2 2 0 01-2-2V6a2 2 0 012-2h6l5 5v9a2 2 0 01-2 2z" />
          </svg>
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-zinc-200 dark:divide-white/10">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200/70 dark:divide-white/10">
          {rows.map((row, index) => (
            <tr key={row.id || `${index}-${columns[0]?.key || 'row'}`} className="align-top">
              {columns.map((column) => (
                <td key={column.key} className="px-4 py-4 text-sm text-zinc-700 dark:text-zinc-300">
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

export const formatDateOnly = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString();
};

export const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0));

export const resolveMediaSrc = (value) => {
  if (!value) return null;
  if (String(value).startsWith('http://') || String(value).startsWith('https://')) {
    return value;
  }
  if (String(value).startsWith('/')) {
    return value;
  }
  return `/${String(value).replace(/^\/+/, '')}`;
};

export { ActionButton, EmptyState, ErrorState, StatusBadge, Card, CardHeader };
